import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

import { MatCheckboxModule } from "@angular/material/checkbox";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { NotificationService } from "../../../../services/notification.service";
import { ProtocolAttachment } from "../../../../entities/protocol-attachment";
import { ProtocolService } from "../../../../services/protocol.service";
import { finalize, first } from "rxjs";
import { InstituteService } from "../../../../services/institute.service";
import { TranslatePipe, TranslateService } from "@tolgee/ngx";
import { HelpPanelService } from "../../../help-center/help-panel.service";
import { markEmailDomainError } from "../../../../validators/email-domain-error";

@Component({
	selector: "app-protocol-attachment-share",
	imports: [
		MatButtonModule,
		MatDialogClose,
		MatIconModule,
		MatDialogContent,
		MatProgressSpinnerModule,
		MatDialogTitle,
		MatCheckboxModule,
		FormsModule,
		MatDialogActions,
		MatInputModule,
		ReactiveFormsModule,
		TranslatePipe,
	],
	templateUrl: "./protocol-attachment-share.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./protocol-attachment-share.component.scss",
})
export class ProtocolAttachmentShareComponent {
	private data = inject<{
		protocolId: number;
		attachment: ProtocolAttachment;
	}>(MAT_DIALOG_DATA);
	private formBuilder = inject(UntypedFormBuilder);
	private protocolService = inject(ProtocolService);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);
	private helpPanel = inject(HelpPanelService);
	private dialogRef = inject<MatDialogRef<ProtocolAttachmentShareComponent>>(MatDialogRef);

	isLoading = false;
	formGroup: UntypedFormGroup;

	constructor() {
		const instituteService = inject(InstituteService);

		this.formGroup = this.formBuilder.group({
			email: ["", [Validators.required, Validators.email]],
			isSecureEmail: [false],
		});
		instituteService
			.isSecurity()
			.pipe(first())
			.subscribe({
				next: (response) => {
					if (!response.security) {
						this.formGroup.get("isSecureEmail")?.setValue(false);
						this.formGroup.get("isSecureEmail")?.disable();
					}
				},
			});
	}

	share(): void {
		this.isLoading = true;
		this.protocolService
			.shareProtocolAttachment(
				this.data.protocolId,
				this.data.attachment.id,
				this.formGroup.get("email").value,
				this.formGroup.get("isSecureEmail").value
			)
			.pipe(
				first(),
				finalize(() => (this.isLoading = false))
			)
			.subscribe({
				next: (response) => {
					if (response.status === 204) {
						this.toastr.success(this.translate.instant("protocol.attachments.share.success"));
						this.dialogRef.close();
					} else {
						this.toastr.error(this.translate.instant("protocol.attachments.share.error"));
					}
				},
				error: (error) => markEmailDomainError(error, this.formGroup.get("email")),
			});
	}

	showHelp(): void {
		this.helpPanel.open("security.secure-email");
	}
}
